/**
 * editorial.ts — Modelos no vocabulário editorial (capa, recursos e etapas)
 *
 * Estreiam o bloco de empilhamento (`layout.stack`), os títulos de capa,
 * o selo com traço, os botões em caixa alta com seta e os cartões com ícone
 * do próprio sistema. Nenhum HTML, script ou fonte externa é usado.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (Studio, fidelidade editorial)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, AVATARES, WEB, b } from './_shared';

const modelos: StudioPatternDefinition[] = [
  {
    key: 'capa.editorial-vertice',
    category: 'capa',
    label: 'Capa editorial com prova social',
    description: 'Título grande em caixa alta, selo com traço, botões com seta e prova social.',
    preview: 'split-right',
    outputs: WEB,
    order: 40,
    build: () => [
      b(
        'section.layered',
        {
          layout: 'split',
          paddingY: 'xl',
          align: 'left',
          maxWidth: '1180px',
          backgroundColor: '#0b0f14',
          textColor: '#f8fafc',
          glow: 'left',
          motion: 'fade-up',
        },
        [
          b('layout.stack', { gap: 'md', align: 'start' }, [
            b('ui.badge', { text: 'Operação sob controle', variant: 'line', rule: true }),
            b('ui.title', {
              text: 'Rotina previsível para times que não podem parar',
              size: 'hero',
              level: 'h1',
              transform: 'uppercase',
              leading: 'tight',
              maxWidth: '18ch',
            }),
            b('ui.paragraph', {
              text: 'Um só lugar para acompanhar atendimento, vendas e pós-venda, com indicadores no mesmo painel.',
              maxWidth: '46ch',
            }),
            b('ui.buttons', {
              items: 'Falar com especialista|#formulario|solid\nVer como funciona|#etapas|ghost',
              size: 'lg',
              shape: 'square',
              uppercase: true,
              arrow: true,
            }),
            b('ui.avatars', {
              items: AVATARES,
              text: 'Mais de 400 equipes já operam por aqui',
              rating: '4,9',
              max: 3,
            }),
          ]),
          b('ui.image', {
            src: FOTO.painel,
            alt: 'Painel de acompanhamento da operação',
            ratio: 'portrait',
            radius: 'lg',
            shadow: 'lg',
          }),
        ],
      ),
    ],
  },

  {
    key: 'recursos.cartoes-editoriais',
    category: 'recursos',
    label: 'Cartões editoriais com ícone',
    description: 'Três cartões numerados, com ícone do sistema e realce no primeiro.',
    preview: 'grid-3',
    outputs: WEB,
    order: 40,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'left', maxWidth: '1180px', backgroundColor: '#0b0f14', textColor: '#f8fafc' },
        [
          b('layout.stack', { gap: 'sm', align: 'start' }, [
            b('ui.badge', { text: 'O que você ganha', variant: 'line', rule: true }),
            b('ui.title', { text: 'Menos improviso, mais previsibilidade', size: 'lg', level: 'h2' }),
          ]),
          b('layout.columns', { columns: '4|4|4', gap: 'md' }, [
            b('ui.card', {
              variant: 'editorial',
              iconName: 'agilidade',
              index: '01',
              eyebrow: 'Atendimento',
              title: 'Respostas no tempo certo',
              text: 'Filas, prazos e responsáveis definidos, sem depender da memória de ninguém.',
              highlighted: true,
              hover: true,
              minHeight: 'md',
            }),
            b('ui.card', {
              variant: 'editorial',
              iconName: 'resultados',
              index: '02',
              eyebrow: 'Vendas',
              title: 'Funil sempre atualizado',
              text: 'Cada oportunidade com etapa, valor e próximo passo visíveis para o time.',
              hover: true,
              minHeight: 'md',
            }),
            b('ui.card', {
              variant: 'editorial',
              iconName: 'escudo',
              index: '03',
              eyebrow: 'Governança',
              title: 'Acesso por perfil',
              text: 'Cada pessoa enxerga apenas o que precisa, com histórico de tudo que muda.',
              hover: true,
              minHeight: 'md',
            }),
          ]),
        ],
      ),
    ],
  },

  {
    key: 'etapas.coluna-fixa',
    category: 'etapas',
    label: 'Etapas com coluna fixa',
    description: 'Texto de apoio que acompanha a rolagem ao lado das etapas numeradas.',
    preview: 'split-left',
    outputs: WEB,
    order: 40,
    build: () => [
      b(
        'section.layered',
        { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px' },
        [
          b('layout.stack', { gap: 'sm', align: 'start', sticky: true, border: 'left' }, [
            b('ui.badge', { text: 'Como funciona', variant: 'line', rule: true }),
            b('ui.title', { text: 'Três passos até a primeira entrega', size: 'lg', level: 'h2' }),
            b('ui.paragraph', { text: 'Implantação acompanhada, sem parar a operação do dia a dia.' }),
            b('ui.buttons', { items: 'Começar agora|#formulario|solid', uppercase: true, arrow: true }),
          ]),
          b('layout.stack', { gap: 'md', align: 'stretch' }, [
            b('ui.card', {
              variant: 'editorial',
              index: '01',
              title: 'Diagnóstico',
              text: 'Mapeamos o processo atual e o que precisa mudar primeiro.',
            }),
            b('ui.card', {
              variant: 'editorial',
              index: '02',
              title: 'Configuração',
              text: 'Deixamos funis, filas e permissões prontos para o seu time.',
            }),
            b('ui.card', {
              variant: 'editorial',
              index: '03',
              title: 'Acompanhamento',
              text: 'Revisão dos indicadores nas primeiras semanas de uso.',
            }),
          ]),
        ],
      ),
    ],
  },
];

registerPatterns(modelos);
